/**
 * @description      : Search page with Leaflet map showing property listings
 * @author           : Saif
 * @group            : 
 * @created          : 14/11/2024
 * 
 * MODIFICATION LOG
 * - Version         : 1.0.0
 * - Date            : 14/11/2024
 * - Author          : Saif
 * - Modification    : 
**/
import React, { useState, useEffect, useRef } from 'react';
import { WebLayout } from '../../Components/WebLayout';
import { useGet } from '../../Api';
import ListingCard from '../../Components/ListingCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilter, 
  faSearch, 
  faBed, 
  faBath, 
  faUsers,
  faMapMarkerAlt,
  faChevronLeft,
  faChevronRight,
  faArrowUp
} from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ListingFilterModal } from '../../Components/ListingFilterModal';
import { base_url } from '../../Api/apiConfig';
import './style.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon with price
const createCustomIcon = (price) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div class="marker-price">$${price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/night</div>`,
    iconSize: [120, 30],
    iconAnchor: [60, 30],
  });
};

// City to coordinates mapping (shared across component)
const cityCoordinatesMap = {
  'new orleans': [29.9511, -90.0715],
  'punta cana': [18.5819, -68.4049],
  'miami': [25.7617, -80.1918],
  'los angeles': [34.0522, -118.2437],
  'new york': [40.7128, -74.0060],
  'chicago': [41.8781, -87.6298],
  'houston': [29.7604, -95.3698],
  'phoenix': [33.4484, -112.0740],
  'philadelphia': [39.9526, -75.1652],
  'san antonio': [29.4241, -98.4936],
  'san diego': [32.7157, -117.1611],
  'dallas': [32.7767, -96.7970],
  'san jose': [37.3382, -121.8863],
  'austin': [30.2672, -97.7431],
  'jacksonville': [30.3322, -81.6557],
  'san francisco': [37.7749, -122.4194],
  'indianapolis': [39.7684, -86.1581],
  'columbus': [39.9612, -82.9988],
  'fort worth': [32.7555, -97.3308],
  'charlotte': [35.2271, -80.8431],
  'seattle': [47.6062, -122.3321],
  'denver': [39.7392, -104.9903],
  'washington': [38.9072, -77.0369],
  'boston': [42.3601, -71.0589],
  'el paso': [31.7619, -106.4850],
  'detroit': [42.3314, -83.0458],
  'nashville': [36.1627, -86.7816],
  'portland': [45.5152, -122.6784],
  'oklahoma city': [35.4676, -97.5164],
  'las vegas': [36.1699, -115.1398],
  'memphis': [35.1495, -90.0490],
  'louisville': [38.2527, -85.7585],
  'baltimore': [39.2904, -76.6122],
  'milwaukee': [43.0389, -87.9065],
  'albuquerque': [35.0844, -106.6504],
  'tucson': [32.2226, -110.9747],
  'fresno': [36.7378, -119.7871],
  'sacramento': [38.5816, -121.4944],
  'kansas city': [39.0997, -94.5786],
  'mesa': [33.4152, -111.8315],
  'atlanta': [33.7490, -84.3880],
  'omaha': [41.2565, -95.9345],
  'colorado springs': [38.8339, -104.8214],
  'raleigh': [35.7796, -78.6382],
  'virginia beach': [36.8529, -75.9780],
  'miami beach': [25.7907, -80.1300],
  'orlando': [28.5383, -81.3792],
  'tampa': [27.9506, -82.4572],
  'minneapolis': [44.9778, -93.2650],
  'oakland': [37.8044, -122.2712],
  'tulsa': [36.1540, -95.9928],
  'cleveland': [41.4993, -81.6944],
  'wichita': [37.6872, -97.3301],
  'arlington': [32.7357, -97.1081],
};

// Component to handle map view updates
const MapViewUpdater = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  
  return null;
};

export const Search = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Destination dropdown state
  const [countriesList, setCountriesList] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [countriesError, setCountriesError] = useState(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const destinationRef = useRef(null);
  
  // Search filters
  const [location, setLocation] = useState('');
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const checkInRef = useRef(null);
  const checkOutRef = useRef(null);

  const handleCheckInDateChange = (date) => {
    setCheckInDate(date);
    if (checkOutDate && date && checkOutDate < date) {
      setCheckOutDate(null);
    }
    setTimeout(() => checkInRef.current?.setOpen(false, true), 100);
  };

  const handleCheckOutDateChange = (date) => {
    setCheckOutDate(date);
    setTimeout(() => checkOutRef.current?.setOpen(false, true), 100);
  };
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [bedrooms, setBedrooms] = useState('');
  const [placeId, setPlaceId] = useState('');
  const [pets, setPets] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [radius, setRadius] = useState(10);
  const [sortBy, setSortBy] = useState('featured');
  const [selectedListing, setSelectedListing] = useState(null);
  const [mapCenter, setMapCenter] = useState([18.5819, -68.4049]); // Default: Punta Cana
  const [mapZoom, setMapZoom] = useState(6);
  const [showGuestDropdown, setShowGuestDropdown] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [roomList, setRoomList] = useState([]);
  const [searchTrigger, setSearchTrigger] = useState(0);
  
  const { ApiData: ListingData, loading: ListingLoading, get: GetListing } = useGet(`/frontend/listings`);
  const { ApiData: AmenitiesData, get: GetAmenities } = useGet(`/amenities`);
  const { ApiData: PlaceData, get: GetPlace } = useGet(`/places`);
  const mapRef = useRef(null);
  const guestDropdownRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    GetAmenities();
    GetPlace();
  }, []);

  // Fetch listings whenever URL search params change or search is explicitly triggered.
  useEffect(() => {
    const params = searchParams.toString();
    GetListing(`/frontend/listings${params ? `?${params}` : ''}`);
  }, [searchParams, searchTrigger]);

  useEffect(() => {
    if (AmenitiesData) setAmenitiesList(AmenitiesData?.data?.data || []);
  }, [AmenitiesData]);

  useEffect(() => {
    if (PlaceData) setRoomList(PlaceData?.data?.data || []);
  }, [PlaceData]);

  useEffect(() => {
    const loc = searchParams.get('location');
    const checkIn = searchParams.get('check_in');
    const checkOut = searchParams.get('check_out');
    const guests = searchParams.get('guests');
    const bedroomsParam = searchParams.get('bedrooms');
    const placeIdParam = searchParams.get('place_id');
    const petsParam = searchParams.get('pets');
    const minPriceParam = searchParams.get('min_price');
    const maxPriceParam = searchParams.get('max_price');
    const amenitiesParam = searchParams.get('amenities');

    if (loc) setLocation(loc);
    if (checkIn) {
      const [y, m, d] = checkIn.split('-').map(Number);
      const parsed = new Date(y, m - 1, d);
      if (!isNaN(parsed.getTime())) setCheckInDate(parsed);
    }
    if (checkOut) {
      const [y, m, d] = checkOut.split('-').map(Number);
      const parsed = new Date(y, m - 1, d);
      if (!isNaN(parsed.getTime())) setCheckOutDate(parsed);
    }
    if (guests) setAdults(Math.max(1, parseInt(guests, 10) || 1));
    if (bedroomsParam) setBedrooms(bedroomsParam);
    if (placeIdParam) setPlaceId(placeIdParam);
    if (petsParam) setPets(petsParam);
    if (minPriceParam) setMinPrice(minPriceParam);
    if (maxPriceParam) setMaxPrice(maxPriceParam);
    if (amenitiesParam) setSelectedAmenities(amenitiesParam.split(',').filter(Boolean).map(String));
  }, [searchParams]);

  // Fetch destination countries for the location dropdown
  useEffect(() => {
    setCountriesLoading(true);
    setCountriesError(null);
    fetch(`${base_url}/countries`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((res) => {
        const raw = res?.data?.data ?? res?.data ?? res ?? [];
        const list = Array.isArray(raw)
          ? raw.map((item) => ({
              id: item.id,
              name: (item.name ?? item.country_name ?? item.title ?? '').trim(),
            })).filter((item) => item.name)
          : [];
        setCountriesList(list);
        setCountriesLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch countries:', err);
        setCountriesError('Failed to load countries. Please refresh the page.');
        setCountriesList([]);
        setCountriesLoading(false);
      });
  }, []);

  const filteredCountries = location?.trim()
    ? countriesList.filter((c) =>
        c.name.toLowerCase().includes(location.trim().toLowerCase())
      )
    : countriesList;

  // Close destination dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (destinationRef.current && !destinationRef.current.contains(e.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const applyFilters = (sourceListings = listings) => {
    // Backend now handles filtering; this function only sorts the returned results.
    let filtered = [...sourceListings];

    if (sortBy === 'price_low') {
      filtered.sort((a, b) => parseFloat(a?.pricing?.nightly || 0) - parseFloat(b?.pricing?.nightly || 0));
    } else if (sortBy === 'price_high') {
      filtered.sort((a, b) => parseFloat(b?.pricing?.nightly || 0) - parseFloat(a?.pricing?.nightly || 0));
    }

    setFilteredListings(filtered);
    return filtered;
  };

  useEffect(() => {
    if (ListingData) {
      const listingsData = ListingData?.data?.listings?.data || [];
      setListings(listingsData);
      
      // Calculate center point from all listings to show all markers
      if (listingsData.length > 0) {
        const tempGetCoords = (listing, index) => {
          if (listing?.location?.latitude && listing?.location?.longitude) {
            return [parseFloat(listing.location.latitude), parseFloat(listing.location.longitude)];
          }
          const city = listing?.location?.city?.toLowerCase() || '';
          const country = listing?.location?.country?.toLowerCase() || '';
          let baseCoords = null;
          
          for (const [cityName, coords] of Object.entries(cityCoordinatesMap)) {
            if (city.includes(cityName) || cityName.includes(city)) {
              baseCoords = coords;
              break;
            }
          }
          
          if (!baseCoords) {
            if (country.includes('united states') || country.includes('usa') || country.includes('us')) {
              baseCoords = [39.8283, -98.5795];
            } else if (country.includes('dominican')) {
              baseCoords = [18.5819, -68.4049];
            } else {
              baseCoords = [18.5819, -68.4049];
            }
          }
          
          const offsetLat = (Math.sin(index * 2.4) * 0.05);
          const offsetLng = (Math.cos(index * 2.4) * 0.05);
          return [baseCoords[0] + offsetLat, baseCoords[1] + offsetLng];
        };
        
        // Calculate average center of all listings
        const allCoords = listingsData.map((listing, index) => tempGetCoords(listing, index));
        const avgLat = allCoords.reduce((sum, coord) => sum + coord[0], 0) / allCoords.length;
        const avgLng = allCoords.reduce((sum, coord) => sum + coord[1], 0) / allCoords.length;
        
        setMapCenter([avgLat, avgLng]);
        // Set zoom level based on number of listings (more listings = zoom out more)
        if (listingsData.length > 5) {
          setMapZoom(12);
        } else if (listingsData.length > 2) {
          setMapZoom(6);
        } else {
          setMapZoom(10);
        }
      }
    }
  }, [ListingData]);

  useEffect(() => {
    if (listings.length) {
      applyFilters(listings);
    } else {
      setFilteredListings([]);
    }
  }, [listings, sortBy]);

  // Handle scroll to top
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside guest dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (guestDropdownRef.current && !guestDropdownRef.current.contains(event.target)) {
        setShowGuestDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const buildSearchParams = () => {
    const params = new URLSearchParams();

    if (location?.trim()) params.set('location', location.trim());
    if (checkInDate) {
      const y = checkInDate.getFullYear();
      const m = String(checkInDate.getMonth() + 1).padStart(2, '0');
      const d = String(checkInDate.getDate()).padStart(2, '0');
      params.set('check_in', `${y}-${m}-${d}`);
    }
    if (checkOutDate) {
      const y = checkOutDate.getFullYear();
      const m = String(checkOutDate.getMonth() + 1).padStart(2, '0');
      const d = String(checkOutDate.getDate()).padStart(2, '0');
      params.set('check_out', `${y}-${m}-${d}`);
    }
    if (adults > 0) params.set('guests', String(adults));
    if (placeId) params.set('place_id', placeId);
    if (bedrooms) params.set('bedrooms', bedrooms);
    if (pets) params.set('pets', pets);
    if (minPrice) params.set('min_price', minPrice);
    if (maxPrice) params.set('max_price', maxPrice);
    if (selectedAmenities.length) params.set('amenities', selectedAmenities.join(','));

    return params;
  };

  const handleSearch = () => {
    const params = buildSearchParams();
    const queryString = params.toString();
    navigate(`/search${queryString ? `?${queryString}` : ''}`);
    // Force re-fetch even if the URL params are identical so repeated searches always refresh.
    setSearchTrigger((prev) => prev + 1);
  };

  const handleFilterSubmit = () => {
    const params = buildSearchParams();

    setShowFilterModal(false);
    navigate(`/search?${params.toString()}`);
    // Force re-fetch even if the URL params are identical.
    setSearchTrigger((prev) => prev + 1);
  };

  const handleListingClick = (listing) => {
    setSelectedListing(listing);
    // If listing has coordinates, center map on it
    if (listing?.location?.latitude && listing?.location?.longitude) {
      setMapCenter([parseFloat(listing.location.latitude), parseFloat(listing.location.longitude)]);
      setMapZoom(12);
    }
  };

  const incrementAdults = () => setAdults(prev => prev + 1);
  const decrementAdults = () => setAdults(prev => Math.max(1, prev - 1));
  const incrementChildren = () => setChildren(prev => prev + 1);
  const decrementChildren = () => setChildren(prev => Math.max(0, prev - 1));

  // Calculate total guests from bedrooms
  const getTotalGuests = (listing) => {
    if (listing?.bedrooms && Array.isArray(listing.bedrooms)) {
      return listing.bedrooms.reduce((sum, bedroom) => sum + parseInt(bedroom?.no_of_guests || 0), 0);
    }
    return 0;
  };

  // Get listing coordinates (fallback to city-based if not available)
  const getListingCoordinates = (listing, index = 0) => {
    // First, check if listing has explicit coordinates
    if (listing?.location?.latitude && listing?.location?.longitude) {
      return [parseFloat(listing.location.latitude), parseFloat(listing.location.longitude)];
    }
    
    // Fallback: Use city-based coordinates
    const city = listing?.location?.city?.toLowerCase() || '';
    const country = listing?.location?.country?.toLowerCase() || '';
    
    // Try to find city in mapping
    let baseCoordinates = null;
    
    // Check city name
    for (const [cityName, coords] of Object.entries(cityCoordinatesMap)) {
      if (city.includes(cityName) || cityName.includes(city)) {
        baseCoordinates = coords;
        break;
      }
    }
    
    // If not found, try country-based defaults
    if (!baseCoordinates) {
      if (country.includes('united states') || country.includes('usa') || country.includes('us')) {
        baseCoordinates = [39.8283, -98.5795]; // Center of USA
      } else if (country.includes('dominican')) {
        baseCoordinates = [18.5819, -68.4049]; // Punta Cana
      } else if (country.includes('mexico')) {
        baseCoordinates = [19.4326, -99.1332]; // Mexico City
      } else if (country.includes('canada')) {
        baseCoordinates = [56.1304, -106.3468]; // Center of Canada
      } else {
        baseCoordinates = [18.5819, -68.4049]; // Default to Punta Cana
      }
    }
    
    // Add a small random offset based on index to prevent overlapping markers
    // This spreads out listings in the same city
    const offsetLat = (Math.sin(index * 2.4) * 0.05); // ~5.5km variation
    const offsetLng = (Math.cos(index * 2.4) * 0.05);
    
    return [
      baseCoordinates[0] + offsetLat,
      baseCoordinates[1] + offsetLng
    ];
  };

  return (
    <WebLayout>
      <div className="search-page">
        {/* Search and Filter Panel */}
        <div className="search-filter-panel">
          <div className="container-fluid">
            <div className="row g-2 align-items-end search-filter-row">
              <div className="col position-relative" ref={destinationRef}>
                <label className="search-label">Where</label>
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Destination"
                  value={location}
                  onChange={(e) => {
                    setLocation(e.target.value);
                    setShowCountryDropdown(true);
                  }}
                  onFocus={() => setShowCountryDropdown(true)}
                  autoComplete="off"
                />
                {showCountryDropdown && (
                  <ul
                    className="list-group border shadow-sm position-absolute w-100 mt-1 overflow-auto rounded"
                    style={{ maxHeight: '220px', zIndex: 1050 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {countriesLoading ? (
                      <li className="list-group-item text-muted small">Loading countries...</li>
                    ) : countriesError ? (
                      <li className="list-group-item text-danger small">{countriesError}</li>
                    ) : filteredCountries.length ? (
                      filteredCountries.map((country) => (
                        <li
                          key={country.id}
                          className="list-group-item list-group-item-action"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setLocation(country.name);
                            setShowCountryDropdown(false);
                          }}
                        >
                          {country.name}
                        </li>
                      ))
                    ) : location?.trim() ? (
                      <li className="list-group-item text-muted small">No country found</li>
                    ) : null}
                  </ul>
                )}
              </div>
              <div className="col">
                <label className="search-label">Check in</label>
                <div onClick={(e) => e.stopPropagation()}>
                  <DatePicker
                    ref={checkInRef}
                    selected={checkInDate}
                    shouldCloseOnSelect
                    preventOpenOnFocus
                    onInputClick={() => {
                      checkOutRef.current?.setOpen(false);
                      checkInRef.current?.setOpen(true);
                    }}
                    onClickOutside={() => checkInRef.current?.setOpen(false)}
                    onChange={handleCheckInDateChange}
                    placeholderText="Date"
                    className="form-control search-input"
                    dateFormat="MM/dd/yyyy"
                    minDate={new Date()}
                    selectsStart
                    startDate={checkInDate}
                    endDate={checkOutDate}
                  />
                </div>
              </div>
              <div className="col">
                <label className="search-label">Check out</label>
                <div onClick={(e) => e.stopPropagation()}>
                  <DatePicker
                    ref={checkOutRef}
                    selected={checkOutDate}
                    shouldCloseOnSelect
                    preventOpenOnFocus
                    onInputClick={() => {
                      checkInRef.current?.setOpen(false);
                      checkOutRef.current?.setOpen(true);
                    }}
                    onClickOutside={() => checkOutRef.current?.setOpen(false)}
                    onChange={handleCheckOutDateChange}
                    placeholderText="Date"
                    className="form-control search-input"
                    dateFormat="MM/dd/yyyy"
                    minDate={checkInDate || new Date()}
                    selectsEnd
                    startDate={checkInDate}
                    endDate={checkOutDate}
                  />
                </div>
              </div>
              <div className="col">
                <label className="search-label">Guests</label>
                <div className="guest-selector" ref={guestDropdownRef}>
                  <div 
                    className="guest-display"
                    onClick={() => setShowGuestDropdown(!showGuestDropdown)}
                  >
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    <span>{adults} {children > 0 && `+ ${children}`}</span>
                  </div>
                  {showGuestDropdown && (
                    <div className="guest-dropdown">
                      <div className="guest-control">
                        <label>Adults</label>
                        <div className="guest-buttons">
                          <button type="button" onClick={decrementAdults}>-</button>
                          <span>{adults}</span>
                          <button type="button" onClick={incrementAdults}>+</button>
                        </div>
                      </div>
                      <div className="guest-control">
                        <label>Children</label>
                        <div className="guest-buttons">
                          <button type="button" onClick={decrementChildren}>-</button>
                          <span>{children}</span>
                          <button type="button" onClick={incrementChildren}>+</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="col">
                <label className="search-label">Bedrooms</label>
                <input
                  type="number"
                  className="form-control search-input"
                  placeholder="Any"
                  value={bedrooms}
                  onChange={(e) => setBedrooms(e.target.value)}
                  min="1"
                />
              </div>
              <div className="col">
                <label className="search-label">Min Price</label>
                <input
                  type="number"
                  className="form-control search-input"
                  placeholder="Min"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                />
              </div>
              <div className="col">
                <label className="search-label">Max Price</label>
                <input
                  type="number"
                  className="form-control search-input"
                  placeholder="Max"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                />
              </div>
              <div className="col">
                <label className="search-label">&nbsp;</label>
                <button type="button" className="btn btn-outline-secondary w-100" onClick={() => setShowFilterModal(true)}>
                  <FontAwesomeIcon icon={faFilter} /> Filter
                </button>
              </div>
              <div className="col">
                <label className="search-label">&nbsp;</label>
                <button className="btn btn-primary search-btn w-100" onClick={handleSearch}>
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </div>
            </div>
            {/* Radius hidden temporarily as requested */}
            {/*
            <div className="row g-2 mt-2 align-items-center">
              <div className="col-md-3">
                <label className="search-label">Radius: {radius} km</label>
                <input
                  type="range"
                  className="form-range"
                  min="1"
                  max="50"
                  value={radius}
                  onChange={(e) => setRadius(parseInt(e.target.value))}
                />
              </div>
            </div>
            */}
          </div>
        </div>

        {/* Main Content: Listings and Map */}
        <div className="search-content">
          <div className="container-fluid">
            <div className="row">
              {/* Left Side: Property Listings */}
              <div className="col-md-5 listings-column">
                <div className="listings-header">
                  <h3>{filteredListings.length} Rentals</h3>
                  <select
                    className="form-select sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="featured">Sort By: Featured First</option>
                    <option value="price_low">Price: Low to High</option>
                    <option value="price_high">Price: High to Low</option>
                  </select>
                </div>
                <div className="listings-container">
                  {ListingLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : filteredListings.length === 0 ? (
                    <div className="text-center py-5">
                      <p className="text-muted">No properties found</p>
                    </div>
                  ) : (
                    filteredListings.map((listing, index) => (
                      <div
                        key={listing?.id || index}
                        className={`listing-item ${selectedListing?.id === listing?.id ? 'active' : ''}`}
                        onClick={() => handleListingClick(listing)}
                      >
                        <ListingCard data={listing} />
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Right Side: Map */}
              <div className="col-md-7 map-column">
                <div className="map-container">
                  <MapContainer
                    center={mapCenter}
                    zoom={mapZoom}
                    style={{ height: '100%', width: '100%' }}
                    ref={mapRef}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapViewUpdater center={mapCenter} zoom={mapZoom} />
                    {filteredListings.map((listing, index) => {
                      const coordinates = getListingCoordinates(listing, index);
                      const price = parseFloat(listing?.pricing?.nightly || 0);
                      const totalGuestsCount = getTotalGuests(listing);
                      
                      return (
                        <Marker
                          key={listing?.id || index}
                          position={coordinates}
                          icon={createCustomIcon(price)}
                          eventHandlers={{
                            click: () => handleListingClick(listing),
                          }}
                        >
                          <Popup className="property-popup">
                            <div className="popup-content">
                              {(listing?.media?.length > 0 || listing?.linked_media?.length > 0) && (
                                <img
                                  src={listing.media[0]?.image_url || listing.linked_media[0]?.image_url}
                                  alt={listing?.title}
                                  className="popup-image"
                                />
                              )}
                              <h6 className="popup-title">{listing?.title}</h6>
                              <p className="popup-location">
                                <FontAwesomeIcon icon={faMapMarkerAlt} />{' '}
                                {listing?.location?.city}, {listing?.location?.country}
                              </p>
                              <div className="popup-price">
                                ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/night
                              </div>
                              <div className="popup-details">
                                <span>
                                  <FontAwesomeIcon icon={faBed} /> {listing?.bedrooms?.length || 0} Bedrooms
                                </span>
                                <span>
                                  <FontAwesomeIcon icon={faBath} /> {listing?.bathrooms || 0} Baths
                                </span>
                                <span>
                                  <FontAwesomeIcon icon={faUsers} /> {totalGuestsCount} Guests
                                </span>
                              </div>
                              {listing?.type_of_listing && (
                                <p className="popup-type">{listing.type_of_listing?.listing_name || 'Property'}</p>
                              )}
                              <Link
                                to={`/listing-detial/${listing?.id}`}
                                className="btn btn-primary btn-sm mt-2 w-100"
                              >
                                View Details
                              </Link>
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MapContainer>
                  
                  {/* Map Navigation Controls */}
                  <div className="map-controls">
                    <button className="map-nav-btn prev-btn">
                      <FontAwesomeIcon icon={faChevronLeft} /> PREV
                    </button>
                    <button className="map-nav-btn next-btn">
                      NEXT <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button className="scroll-to-top" onClick={scrollToTop}>
            <FontAwesomeIcon icon={faArrowUp} />
          </button>
        )}
      </div>

      <ListingFilterModal
        show={showFilterModal}
        close={() => setShowFilterModal(false)}
        amenitiesList={amenitiesList}
        roomList={roomList}
        filters={{
          place_id: placeId,
          rooms: bedrooms,
          pets,
          min_price: minPrice,
          max_price: maxPrice,
          amenities: selectedAmenities,
        }}
        onChange={(filters) => {
          setPlaceId(filters.place_id || '');
          setBedrooms(filters.rooms || '');
          setPets(filters.pets || '');
          setMinPrice(filters.min_price || '');
          setMaxPrice(filters.max_price || '');
          setSelectedAmenities(filters.amenities || []);
        }}
        onSubmit={handleFilterSubmit}
      />
    </WebLayout>
  );
};

